/**
 * Diagnostik til enhedsfejl (fx iPhone-appens bundmenu/topbar): åbn appen med `?debug=viewport` (eller sæt
 * localStorage plotwise_debug_viewport=1). Viser vinduets mål, safe-area-værdier og standalone-status i et
 * lille panel, så et skærmbillede er nok til at se, om layoutet dækker hele skærmen.
 */
export interface ViewportSnapshot {
  inner: string;
  screen: string;
  visual: string;
  safe: string;
  units: string;
  standalone: string;
  shell: string;
  dpr: number;
  ua: string;
}

export function readViewport(win: Window = window, doc: Document = document): ViewportSnapshot {
  const probe = doc.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  doc.body.appendChild(probe);
  const cs = win.getComputedStyle(probe);
  const safe = `t${cs.paddingTop} r${cs.paddingRight} b${cs.paddingBottom} l${cs.paddingLeft}`;
  const units = ['svh', 'dvh', 'lvh'].map((u) => {
    const d = doc.createElement('div');
    d.style.cssText = `position:fixed;top:0;left:0;width:1px;height:100${u};visibility:hidden`;
    doc.body.appendChild(d);
    const h = Math.round(d.getBoundingClientRect().height);
    d.remove();
    return `${u}${h}`;
  });
  probe.remove();
  const vv = win.visualViewport;
  const nav = win.navigator as Navigator & { standalone?: boolean };
  const mq = win.matchMedia?.('(display-mode: standalone)')?.matches;
  return {
    inner: `${win.innerWidth}x${win.innerHeight}`,
    screen: `${win.screen.width}x${win.screen.height}`,
    visual: vv
      ? `${Math.round(vv.width)}x${Math.round(vv.height)} s${vv.scale.toFixed(2)} top${Math.round(vv.offsetTop)}`
      : 'n/a',
    safe,
    units: units.join(' '),
    standalone: `navigator.standalone=${String(nav.standalone)} display-mode=${String(mq)}`,
    shell: (() => {
      const r = doc.querySelector('.mobile-app-shell')?.getBoundingClientRect();
      return r ? `top${Math.round(r.top)} bottom${Math.round(r.bottom)}` : 'n/a';
    })(),
    dpr: win.devicePixelRatio,
    ua: nav.userAgent.replace(/^Mozilla\/5.0 /, '').slice(0, 70),
  };
}

export function enableViewportDebug(win: Window = window, doc: Document = document): void {
  let on = false;
  try {
    on =
      new URLSearchParams(win.location.search).get('debug') === 'viewport' ||
      win.localStorage.getItem('plotwise_debug_viewport') === '1';
  } catch {
    on = false;
  }
  if (!on) return;
  const el = doc.createElement('pre');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText =
    'position:fixed;left:6px;top:50%;transform:translateY(-50%);z-index:2147483647;margin:0;padding:6px 8px;' +
    'font:11px/1.35 ui-monospace,Menlo,monospace;color:#0f0;background:rgba(0,0,0,.78);border-radius:8px;' +
    'pointer-events:none;white-space:pre-wrap;max-width:92vw';
  const render = () => {
    const s = readViewport(win, doc);
    el.textContent = [
      `inner   ${s.inner}`,
      `screen  ${s.screen}  dpr${s.dpr}`,
      `visual  ${s.visual}`,
      `safe    ${s.safe}`,
      `units   ${s.units}`,
      `shell   ${s.shell}`,
      `app     ${s.standalone}`,
      `ua      ${s.ua}`,
    ].join('\n');
  };
  doc.body.appendChild(el);
  render();
  win.addEventListener('resize', render);
  win.visualViewport?.addEventListener('resize', render);
  win.visualViewport?.addEventListener('scroll', render);
}

const DEBUG_KEY = 'plotwise_debug_viewport';

export function isViewportDebugOn(win: Window = window): boolean {
  try {
    return win.localStorage.getItem(DEBUG_KEY) === '1';
  } catch {
    return false;
  }
}

/** Slår målepanelet til/fra (bruges af admin på profilsiden, fordi hjemmeskærms-appen ikke har en adresselinje). */
export function toggleViewportDebug(win: Window = window): void {
  try {
    if (isViewportDebugOn(win)) win.localStorage.removeItem(DEBUG_KEY);
    else win.localStorage.setItem(DEBUG_KEY, '1');
  } catch {
    return;
  }
  win.location.reload();
}
